import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, ArrowLeft, Loader2, LogOut, RefreshCw, Zap, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const WINDOWS = [
  { label: "24h", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
];

const Card = ({ label, value, sub, testId, tone }) => (
  <div data-testid={testId} className="rounded-lg border border-border bg-card p-4">
    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`mt-1 font-heading text-2xl font-bold ${tone || ""}`}>{value ?? "—"}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const fmtMs = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
const fmtPct = (p) => (p == null ? "—" : `${p}%`);
const fmtNum = (n) => (n == null ? "—" : n.toLocaleString());
const fmtUsd = (n) => (n == null ? "—" : `$${n.toFixed ? n.toFixed(2) : n}`);

export default function PlatformBuffer() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/platform/buffer/report?window_days=${days}`);
      setReport(r.data);
    } catch {
      toast.error("Could not load buffer report.");
    } finally {
      setLoading(false);
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const hitPct = report?.buffer_hit_pct;
  const hitTone = hitPct == null ? "" : hitPct >= 85 ? "text-emerald-500"
                                     : hitPct >= 60 ? "text-amber-500" : "text-red-500";
  const dupTone = report?.duplicate_deliveries_detected > 0 ? "text-red-500" : "text-emerald-500";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))" }}>
              <Activity className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">Haruki Health</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
                Admin · Scenario Buffer
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/platform" className="text-xs text-muted-foreground hover:text-foreground" data-testid="buffer-back-platform">
              <ArrowLeft className="mr-1 inline h-3 w-3" />Platform overview
            </Link>
            <span className="hidden text-xs text-muted-foreground sm:inline">{user?.email}</span>
            <Button data-testid="buffer-logout-button" variant="ghost" size="sm"
                    onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="mr-2 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold">Scenario Buffer Health</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Server-side scenario pre-generation performance.
              {report && (
                <>
                  {" "}Depth <span className="font-mono">{report.buffer_depth}</span>
                  {" · "}cost/call <span className="font-mono">{fmtUsd(report.claude_cost_per_call_usd)}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-md border border-border">
              {WINDOWS.map((w) => (
                <button key={w.days}
                        data-testid={`buffer-window-${w.days}`}
                        onClick={() => setDays(w.days)}
                        className={`px-3 py-1.5 text-xs ${days === w.days ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/40"}`}>
                  {w.label}
                </button>
              ))}
            </div>
            <Button data-testid="buffer-refresh-button" variant="outline" size="sm" onClick={() => load()}>
              <RefreshCw className="mr-1.5 h-3 w-3" /> Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="mt-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !report ? (
          <div className="mt-10 text-sm text-muted-foreground">No data.</div>
        ) : (
          <>
            {report.duplicate_deliveries_detected > 0 && (
              <div data-testid="buffer-duplicate-banner"
                   className="mt-4 flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-500">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <div className="font-medium">Duplicate scenario deliveries detected: {report.duplicate_deliveries_detected}</div>
                  <div className="text-xs opacity-80">Buffer never-serve-twice guarantee may be compromised. Investigate immediately.</div>
                </div>
              </div>
            )}

            <h2 className="mt-6 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">Hit rate</h2>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Card testId="buffer-card-hit-pct" label="Buffer hit %" value={fmtPct(report.buffer_hit_pct)}
                    tone={hitTone} sub={`target ≥ 85%`} />
              <Card testId="buffer-card-miss-pct" label="Buffer miss %" value={fmtPct(report.buffer_miss_pct)}
                    sub={`${fmtNum(report.buffer_misses)} misses`} />
              <Card testId="buffer-card-requests" label="Scenario requests" value={fmtNum(report.scenario_requests)}
                    sub={`window · ${report.window_days}d`} />
              <Card testId="buffer-card-active-users" label="Active users" value={fmtNum(report.active_users)}
                    sub="delivered ≥ 1 scenario" />
              <Card testId="buffer-card-completed" label="Completed scenarios" value={fmtNum(report.completed_scenarios)} />
            </div>

            <h2 className="mt-8 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">User-perceived wait</h2>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Card testId="buffer-card-avg-wait" label="Avg wait" value={fmtMs(report.avg_wait_ms)}
                    sub="hits + inline misses" />
              <Card testId="buffer-card-p95-wait" label="P95 wait" value={fmtMs(report.p95_wait_ms)} />
              <Card testId="buffer-card-avg-gen" label="Avg generation time" value={fmtMs(report.refills?.avg_generation_ms)}
                    sub="server-side, off critical path" />
              <Card testId="buffer-card-p95-gen" label="P95 generation time" value={fmtMs(report.refills?.p95_generation_ms)} />
            </div>

            <h2 className="mt-8 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">Refills</h2>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Card testId="buffer-card-refill-ok" label="Refills OK" value={fmtNum(report.refills?.ok)} />
              <Card testId="buffer-card-refill-fail" label="Refill failures" value={fmtNum(report.refills?.failed)}
                    tone={report.refills?.failed > 0 ? "text-amber-500" : ""} />
              <Card testId="buffer-card-refill-success-rate" label="Refill success rate" value={fmtPct(report.refills?.success_rate_pct)} />
              <Card testId="buffer-card-refill-attempts" label="Avg dedupe attempts / refill"
                    value={report.refills?.avg_generation_attempts ?? "—"} />
            </div>

            <h2 className="mt-8 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">Claude cost</h2>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Card testId="buffer-card-avg-calls" label="Avg Claude calls / scenario"
                    value={report.avg_claude_calls_per_completed_scenario ?? "—"}
                    sub="generation + content score + HMDE" />
              <Card testId="buffer-card-total-calls" label="Total Claude calls"
                    value={fmtNum(report.total_estimated_claude_calls)} sub="in window" />
              <Card testId="buffer-card-total-cost" label="Estimated Claude cost"
                    value={fmtUsd(report.estimated_claude_cost_usd)} />
              <Card testId="buffer-card-cost-per-user" label="Cost per active user"
                    value={fmtUsd(report.estimated_cost_per_active_user_usd)} />
            </div>

            <h2 className="mt-8 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">Duplicate safety</h2>
            <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Card testId="buffer-card-duplicates" label="Duplicate deliveries detected"
                    value={fmtNum(report.duplicate_deliveries_detected)}
                    tone={dupTone}
                    sub="should always read 0" />
            </div>

            <h2 className="mt-8 font-heading text-sm font-bold uppercase tracking-widest text-muted-foreground">Top miss reasons</h2>
            <div className="mt-2 rounded-lg border border-border bg-card" data-testid="buffer-miss-reasons">
              {Object.keys(report.top_miss_reasons || {}).length === 0 ? (
                <div className="px-4 py-6 text-sm text-muted-foreground">No misses recorded in this window.</div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-border bg-muted/30 text-[11px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5">Reason</th>
                      <th className="px-4 py-2.5">Count</th>
                      <th className="px-4 py-2.5">Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.top_miss_reasons).map(([reason, ct]) => (
                      <tr key={reason} data-testid={`buffer-miss-row-${reason}`}
                          className="border-b border-border/60 last:border-0">
                        <td className="px-4 py-2 font-mono text-xs">{reason}</td>
                        <td className="px-4 py-2 font-mono">{ct}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {REASON_EXPLAIN[reason] || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-8 flex items-center gap-2 rounded-md border border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
              <Zap className="h-3 w-3" />
              Costs are estimates using <span className="font-mono">CLAUDE_COST_PER_CALL_USD = {fmtUsd(report.claude_cost_per_call_usd)}</span>.
              Adjust this env value on Railway to match your actual per-call cost.
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const REASON_EXPLAIN = {
  empty_never_warmed: "First request from user — no buffered scenario yet. Expected on session start.",
  generation_in_progress: "Refill still running when request arrived. User outran the buffer.",
  stale_vertical: "User's vertical changed — buffered scenario discarded (safety).",
  category_already_completed: "Buffered category was completed between refill and delivery.",
  duplicate_scenario_hash: "Buffered scenario had already been delivered — dedupe caught it (working as intended).",
  invalidated_unspecified: "Buffered scenario invalidated with no reason code.",
  unspecified: "Miss without a recorded reason (should be rare).",
};
